import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Code2, Loader2, Mail, Lock, User, ShieldOff, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [requestName, setRequestName] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [wantsAccess, setWantsAccess] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  useEffect(() => {
    api.get("/auth/registration-status")
      .then((r) => {
        setRegistrationEnabled(r.data.registration_enabled !== false);
      })
      .catch(() => {})
      .finally(() => setCheckingStatus(false));
  }, []);

  const submitAccessRequest = async (e) => {
    e.preventDefault();
    if (!wantsAccess) {
      toast.warning("Bitte bestätige, dass du Zugang möchtest.");
      return;
    }
    setSubmittingRequest(true);
    try {
      const { data } = await api.post("/auth/access-request", {
        name: requestName,
        email: requestEmail,
      });
      toast.success(data.message);
      setRequestSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmittingRequest(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 10) {
      toast.warning("Das Passwort muss mindestens 10 Zeichen lang sein.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Konto erstellt! Los geht's 🚀");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r-2 border-black order-1">
        <img
          src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
          alt="Bunter Code auf einem Bildschirm"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background order-2">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 bg-primary border-2 border-black rounded-lg flex items-center justify-center nb-shadow-sm">
              <Code2 className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-black text-xl">DevPath</span>
          </Link>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mb-2">Konto erstellen</h1>
          <p className="text-muted-foreground font-medium mb-8">Kostenlos – starte in 30 Sekunden.</p>

          {!checkingStatus && !registrationEnabled ? (
            requestSubmitted ? (
              <div className="nb-card p-6 bg-success flex flex-col items-center text-center gap-3" data-testid="access-request-success">
                <CheckCircle2 className="h-8 w-8" strokeWidth={2.5} />
                <p className="font-bold">Danke! Deine Anfrage wurde übermittelt.</p>
                <p className="text-sm font-medium">Wir prüfen sie manuell und melden uns bei dir.</p>
              </div>
            ) : (
              <div className="nb-card p-6 bg-muted space-y-4" data-testid="registration-disabled">
                <div className="flex flex-col items-center text-center gap-2">
                  <ShieldOff className="h-8 w-8 text-muted-foreground" strokeWidth={2.5} />
                  <p className="font-bold">Die Registrierung ist derzeit deaktiviert.</p>
                  <p className="text-sm text-muted-foreground font-medium">
                    Du kannst trotzdem eine Zugangsanfrage stellen – wir prüfen sie manuell und melden uns bei dir.
                  </p>
                </div>
                <form onSubmit={submitAccessRequest} className="space-y-3 text-left">
                  <div>
                    <label className="font-bold text-sm">Name</label>
                    <input
                      required
                      value={requestName}
                      onChange={(e) => setRequestName(e.target.value)}
                      data-testid="access-request-name"
                      className="w-full mt-1 px-3 py-2 border-2 border-black rounded-lg font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Dein Name"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-sm">E-Mail</label>
                    <input
                      type="email"
                      required
                      value={requestEmail}
                      onChange={(e) => setRequestEmail(e.target.value)}
                      data-testid="access-request-email"
                      className="w-full mt-1 px-3 py-2 border-2 border-black rounded-lg font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="du@beispiel.de"
                    />
                  </div>
                  <label className="flex items-start gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={wantsAccess}
                      onChange={(e) => setWantsAccess(e.target.checked)}
                      data-testid="access-request-consent"
                      className="mt-1 h-4 w-4"
                    />
                    Ich möchte gerne Zugang zur Lernplattform erhalten.
                  </label>
                  <button
                    type="submit"
                    disabled={submittingRequest}
                    data-testid="access-request-submit"
                    className="w-full flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press disabled:opacity-60"
                  >
                    {submittingRequest && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />}
                    Zugangsanfrage senden
                  </button>
                </form>
              </div>
            )
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-bold text-sm">Name</label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="register-name"
                  className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Dein Name"
                />
              </div>
            </div>
            <div>
              <label className="font-bold text-sm">E-Mail</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="register-email"
                  className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="du@beispiel.de"
                />
              </div>
            </div>
            <div>
              <label className="font-bold text-sm">Passwort</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="register-password"
                  className="w-full pl-10 pr-12 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Mind. 10 Zeichen"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" strokeWidth={2.5} />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={2.5} />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />}
              Konto erstellen
            </button>
          </form>
          )}

          <p className="text-center mt-6 font-medium text-sm">
            Schon ein Konto?{" "}
            <Link to="/login" className="font-bold text-secondary underline" data-testid="goto-login">
              Anmelden
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
